import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, doc, getDoc, query, where, updateDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { database, storage, auth } from './firebaseConfig';

import DatePicker, { registerLocale } from 'react-datepicker';
import tr from 'date-fns/locale/tr';
import 'react-datepicker/dist/react-datepicker.css';
import './KurumsalPuantajlar.css';

import Select from 'react-select';
import { debounce } from 'lodash';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { PDFDocument } from 'pdf-lib';
import { parseFlexible, toIsoUtcString, formatTrDateOnly } from '../utils/hopsDate';

registerLocale('tr', tr);

function parseDate(dateStr) {
  return parseFlexible(dateStr) || new Date();
}

/**
 * Çalışma süresini dakikaya çeviren fonksiyon
 */
const parseWorkingTime = (timeString) => {
  if (!timeString || timeString.trim() === '') return 0;
  const input = timeString.toString().trim();

  // Format: "2:30" veya "1:00" (HH:MM)
  if (input.includes(':') && !input.toLowerCase().includes('saat')) {
    const parts = input.split(':');
    if (parts.length === 2) {
      const hours = parseInt(parts[0].trim(), 10) || 0;
      const minutes = parseInt(parts[1].trim(), 10) || 0;
      return hours * 60 + minutes;
    }
  }

  // Format: "1 saat 0 dakika"
  if (input.toLowerCase().includes('saat')) {
    let hours = 0;
    let minutes = 0;
    const saatMatch = input.toLowerCase().match(/(\d+)\s*saat/);
    if (saatMatch) hours = parseInt(saatMatch[1], 10) || 0;
    const dakikaMatch = input.toLowerCase().match(/(\d+)\s*(dakika|dk)/);
    if (dakikaMatch) minutes = parseInt(dakikaMatch[1], 10) || 0;
    return hours * 60 + minutes;
  }

  // Format: Ondalık saat
  const normalized = input.replace(',', '.');
  const decimalHours = parseFloat(normalized);
  if (!isNaN(decimalHours) && normalized.includes('.')) {
    return Math.round(decimalHours * 60);
  }

  // Format: Sadece sayı (dakika)
  const justNumber = parseInt(input, 10);
  if (!isNaN(justNumber)) return justNumber;

  return 0;
};

const KurumsalPuantajlar = () => {
  const [corporateUser, setCorporateUser] = useState(null);
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPdfs, setSelectedPdfs] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchStep, setSearchStep] = useState(''); // Arama adımı bilgisi

  // Pagination state'leri
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Filter state'leri
  const [machineTypeOptions, setMachineTypeOptions] = useState([]);
  const [selectedMachineTypes, setSelectedMachineTypes] = useState([]);
  const [operatorOptions, setOperatorOptions] = useState([]);
  const [selectedOperators, setSelectedOperators] = useState([]);
  const [contractorOptions, setContractorOptions] = useState([]);
  const [selectedContractors, setSelectedContractors] = useState([]);

  // Sıralama state'leri
  const [orderBy, setOrderBy] = useState('Tarih');
  const [order, setOrder] = useState('desc');

  // Vergi numarası doğrulama
  const [taxNumberInput, setTaxNumberInput] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [linkedOwnerEmail, setLinkedOwnerEmail] = useState(null);
  const [matchedCustomerName, setMatchedCustomerName] = useState('');
  const [machinesMap, setMachinesMap] = useState({}); // Makine kodu -> Makine türü eşlemesi
  const [ownerCompanyName, setOwnerCompanyName] = useState(''); // Taşeron firma adı

  const navigate = useNavigate();

  // Debounced date setters
  const debouncedSetStartDate = useMemo(
    () => debounce((date) => setStartDate(date), 300),
    []
  );

  const debouncedSetEndDate = useMemo(
    () => debounce((date) => setEndDate(date), 300),
    []
  );

  // Kurumsal kullanıcı kontrolü
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate('/kurumsal');
        return;
      }

      try {
        const corporateUserRef = doc(database, 'corporateUsers', user.email);
        const corporateUserDoc = await getDoc(corporateUserRef);

        if (!corporateUserDoc.exists()) {
          await signOut(auth);
          navigate('/kurumsal');
          return;
        }

        const userData = corporateUserDoc.data();
        setCorporateUser(userData);
        setTaxNumberInput(userData.taxNumber || '');
        
        // Eğer kullanıcı zaten doğrulanmışsa
        if (userData.isVerified && userData.linkedOwnerEmail) {
          setIsVerified(true);
          setLinkedOwnerEmail(userData.linkedOwnerEmail);
          setMatchedCustomerName(userData.matchedCustomerName || '');
        }
      } catch (error) {
        console.error('Kurumsal kullanıcı kontrolü hatası:', error);
        setError('Kullanıcı bilgileri alınamadı.');
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Vergi numarası doğrulama fonksiyonu
  const verifyTaxNumber = async () => {
    if (!taxNumberInput || taxNumberInput.length < 10) {
      setError('Geçerli bir vergi numarası girin.');
      return;
    }

    setVerificationLoading(true);
    setError(null);
    setSearchStep('Vergi numaranız doğrulanıyor...');

    try {
      // Tüm kullanıcıları getir
      const usersRef = collection(database, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      let foundOwnerEmail = null;
      let foundCustomerName = null;

      // Her kullanıcının customerList'inde vergi numarasını ara
      let searchedCount = 0;
      const totalUsers = usersSnapshot.docs.length;
      
      for (const userDoc of usersSnapshot.docs) {
        const userEmail = userDoc.id;
        searchedCount++;
        setSearchStep(`Verileriniz yükleniyor... (${searchedCount}/${totalUsers})`);
        
        try {
          const customerListRef = collection(database, `users/${userEmail}/customerList`);
          const customerSnapshot = await getDocs(customerListRef);

          for (const customerDoc of customerSnapshot.docs) {
            const customerData = customerDoc.data();
            // vergiNumarasi veya taxNumber alanını kontrol et
            const customerTaxNumber = customerData.vergiNumarasi || customerData.taxNumber || customerData['Vergi Numarası'] || '';
            
            if (customerTaxNumber.toString().replace(/\D/g, '') === taxNumberInput.replace(/\D/g, '')) {
              foundOwnerEmail = userEmail;
              foundCustomerName = customerData['Müşteri Adı'] || customerData.customerName || 'İsimsiz Müşteri';
              break;
            }
          }

          if (foundOwnerEmail) break;
        } catch (err) {
          console.log(`${userEmail} müşteri listesi okunamadı:`, err.message);
        }
      }

      if (foundOwnerEmail) {
        // Eşleşme bulundu
        setIsVerified(true);
        setLinkedOwnerEmail(foundOwnerEmail);
        setMatchedCustomerName(foundCustomerName);

        // Kurumsal kullanıcı dokümanını güncelle
        if (corporateUser && auth.currentUser) {
          const corporateUserRef = doc(database, 'corporateUsers', auth.currentUser.email);
          await updateDoc(corporateUserRef, {
            isVerified: true,
            linkedOwnerEmail: foundOwnerEmail,
            matchedCustomerName: foundCustomerName,
            verifiedAt: toIsoUtcString(new Date())
          });
        }

        setSearchStep('');
      } else {
        setError('Bu vergi numarasına ait müşteri kaydı bulunamadı. Lütfen firma sahibi ile iletişime geçin.');
        setSearchStep('');
      }
    } catch (error) {
      console.error('Vergi numarası doğrulama hatası:', error);
      setError('Doğrulama sırasında bir hata oluştu.');
      setSearchStep('');
    } finally {
      setVerificationLoading(false);
    }
  };

  // Puantajları yükle
  useEffect(() => {
    if (!isVerified || !linkedOwnerEmail || !matchedCustomerName) return;

    const fetchPuantajlar = async () => {
      setLoading(true);
      try {
        // Önce makine bilgilerini çek
        const machinesRef = collection(database, `users/${linkedOwnerEmail}/machines`);
        const machinesSnapshot = await getDocs(machinesRef);
        const machinesMapping = {};
        machinesSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const machineCode = data['MakineAdı'] || data['Makine Adı'] || data.machineName || doc.id;
          const machineType = data['MakineTürü'] || data['Makine Türü'] || data.machineType || data['type'] || machineCode;
          machinesMapping[machineCode] = machineType;
        });
        setMachinesMap(machinesMapping);

        // Taşeron firma adını çek
        const ownerDocRef = doc(database, 'users', linkedOwnerEmail);
        const ownerDoc = await getDoc(ownerDocRef);
        if (ownerDoc.exists()) {
          const ownerData = ownerDoc.data();
          setOwnerCompanyName(ownerData['Firma Adı'] || ownerData.companyName || ownerData.firmaAdi || linkedOwnerEmail.split('@')[0]);
        }

        const puantajlarRef = collection(database, `users/${linkedOwnerEmail}/timeSheets`);
        const puantajSnapshot = await getDocs(puantajlarRef);

        const puantajlarData = puantajSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(puantaj => {
            // Sadece bu müşteriye ait puantajları filtrele
            const puantajCustomer = puantaj['Müşteri Adı'] || puantaj.customerName || '';
            return puantajCustomer.toLowerCase() === matchedCustomerName.toLowerCase();
          });

        // PDF URL'leri ve ek bilgileri işle
        const processedData = puantajlarData.map(puantaj => {
          const machineCode = puantaj['Makine İsmi'] || 'makine';
          const puantajNumber = puantaj['Puantaj Sayısı'] || puantaj.id?.slice(-6) || 'puantaj';
          const pdfName = `${machineCode}-${puantajNumber}.pdf`;

          return {
            ...puantaj,
            pdfName,
            customerName: puantaj['Müşteri Adı'] || matchedCustomerName,
            pdfRef: puantaj['PDF URL'] || null
          };
        });

        setAllData(processedData);
        setFilteredData(processedData);

        // Filtre seçeneklerini oluştur - Makine türüne göre
        const machineTypes = [...new Set(processedData.map(p => {
          const machineCode = p['Makine İsmi'] || '';
          return machinesMapping[machineCode] || machineCode;
        }).filter(Boolean))];
        const operators = [...new Set(processedData.map(p => p['Operatör Adı']).filter(Boolean))];

        setMachineTypeOptions(machineTypes.map(mt => ({ value: mt, label: mt })));
        setOperatorOptions(operators.map(o => ({ value: o, label: o })));
        
        // Taşeron firma seçeneği (şu an tek firma ama ileride genişletilebilir)
        if (ownerDoc.exists()) {
          const ownerData = ownerDoc.data();
          const companyName = ownerData['Firma Adı'] || ownerData.companyName || ownerData.firmaAdi || linkedOwnerEmail.split('@')[0];
          setContractorOptions([{ value: companyName, label: companyName }]);
        }

      } catch (error) {
        console.error('Puantajlar yüklenirken hata:', error);
        setError('Puantajlar yüklenirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    fetchPuantajlar();
  }, [isVerified, linkedOwnerEmail, matchedCustomerName]);

  // Filtreleme
  useEffect(() => {
    let filtered = [...allData];

    // Tarih filtresi
    if (startDate || endDate) {
      filtered = filtered.filter(item => {
        const itemDate = parseDate(item['Tarih']);
        if (startDate && endDate) {
          return itemDate >= startDate && itemDate <= endDate;
        } else if (startDate) {
          return itemDate >= startDate;
        } else if (endDate) {
          return itemDate <= endDate;
        }
        return true;
      });
    }

    // Makine türü filtresi
    if (selectedMachineTypes.length > 0) {
      const selectedValues = selectedMachineTypes.map(m => m.value);
      filtered = filtered.filter(item => {
        const machineCode = item['Makine İsmi'] || '';
        const machineType = machinesMap[machineCode] || machineCode;
        return selectedValues.includes(machineType);
      });
    }

    // Operatör filtresi
    if (selectedOperators.length > 0) {
      const selectedValues = selectedOperators.map(o => o.value);
      filtered = filtered.filter(item => selectedValues.includes(item['Operatör Adı']));
    }

    // Taşeron firma filtresi
    if (selectedContractors.length > 0) {
      // Şu an tek firma olduğu için tüm veriler zaten o firmaya ait
      // İleride birden fazla firma olursa burada filtreleme yapılabilir
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [allData, startDate, endDate, selectedMachineTypes, selectedOperators, selectedContractors, machinesMap]);

  // Sıralama
  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const sortedData = useMemo(() => {
    const sorted = [...filteredData];
    sorted.sort((a, b) => {
      let aValue, bValue;

      if (orderBy === 'Tarih') {
        aValue = parseDate(a['Tarih']);
        bValue = parseDate(b['Tarih']);
      } else {
        aValue = a[orderBy] || '';
        bValue = b[orderBy] || '';
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    return sorted;
  }, [filteredData, orderBy, order]);

  // Pagination
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = sortedData.slice(startIndex, endIndex);

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePreviousPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const handleNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));

  // PDF önizleme
  const handlePdfPreview = async (pdfUrl, pdfName) => {
    if (!pdfUrl) {
      setError('PDF URL bulunamadı.');
      return;
    }

    try {
      // Firebase Storage URL ise direkt kullan
      let finalUrl = pdfUrl;
      
      // Eğer storage reference ise URL'yi al
      if (!pdfUrl.startsWith('http')) {
        const storageRef = ref(storage, pdfUrl);
        finalUrl = await getDownloadURL(storageRef);
      }

      setSelectedPdf({ url: finalUrl, name: pdfName });
    } catch (error) {
      console.error('PDF yüklenirken hata:', error);
      setError('PDF yüklenirken bir hata oluştu.');
    }
  };

  const closePopup = () => setSelectedPdf(null);

  // Checkbox işlemleri
  const handleCheckboxChange = (pdfName) => {
    setSelectedPdfs(prev =>
      prev.includes(pdfName)
        ? prev.filter(name => name !== pdfName)
        : [...prev, pdfName]
    );
  };

  const handleSelectAll = () => {
    // Tüm filtrelenmiş verileri seç (sadece mevcut sayfa değil)
    const allPdfNames = sortedData.map(item => item.pdfName);
    setSelectedPdfs(allPdfNames);
  };

  const handleDeselectAll = () => setSelectedPdfs([]);

  // Toplu PDF indirme
  const handleDownloadSelected = async (asMerged = false) => {
    if (selectedPdfs.length === 0) return;

    setLoading(true);
    try {
      const selectedItems = sortedData.filter(item => selectedPdfs.includes(item.pdfName));
      
      if (asMerged) {
        // PDF'leri birleştir
        const mergedPdf = await PDFDocument.create();
        
        for (const item of selectedItems) {
          if (!item.pdfRef) continue;
          
          try {
            let pdfUrl = item.pdfRef;
            if (!pdfUrl.startsWith('http')) {
              const storageRef = ref(storage, pdfUrl);
              pdfUrl = await getDownloadURL(storageRef);
            }
            
            const response = await fetch(pdfUrl);
            const pdfBytes = await response.arrayBuffer();
            const pdf = await PDFDocument.load(pdfBytes);
            const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
            pages.forEach(page => mergedPdf.addPage(page));
          } catch (err) {
            console.error('PDF birleştirme hatası:', err);
          }
        }
        
        const mergedPdfBytes = await mergedPdf.save();
        const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
        saveAs(blob, `Puantajlar_${formatTrDateOnly(new Date())}.pdf`);
      } else {
        // ZIP olarak indir
        const zip = new JSZip();
        
        for (const item of selectedItems) {
          if (!item.pdfRef) continue;
          
          try {
            let pdfUrl = item.pdfRef;
            if (!pdfUrl.startsWith('http')) {
              const storageRef = ref(storage, pdfUrl);
              pdfUrl = await getDownloadURL(storageRef);
            }
            
            const response = await fetch(pdfUrl);
            const blob = await response.blob();
            zip.file(item.pdfName, blob);
          } catch (err) {
            console.error('PDF indirme hatası:', err);
          }
        }
        
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        saveAs(zipBlob, `Puantajlar_${formatTrDateOnly(new Date())}.zip`);
      }
    } catch (error) {
      console.error('İndirme hatası:', error);
      setError('PDF indirme sırasında bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Çıkış yap
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/kurumsal');
    } catch (error) {
      console.error('Çıkış hatası:', error);
    }
  };

  // Çalışma detaylarını render et
  const renderWorkDetails = (item) => {
    const totalWorkingTime = item['Toplam Çalışma Süresi'] || item.totalWorkingTime || '';
    const workTime = item['Çalışma Süresi'] || '';
    const workDetail = item['Çalışma Detayı'] || '';
    const authorizedName = item['Yetkili Adı'] || '';

    return (
      <div className="work-details">
        {totalWorkingTime && <div><strong>Çalışma Saati:</strong> {totalWorkingTime}</div>}
        {workTime && <div><strong>Süre:</strong> {workTime}</div>}
        {workDetail && <div><strong>Detay:</strong> {workDetail}</div>}
        {authorizedName && <div><strong>Yetkili:</strong> {authorizedName}</div>}
      </div>
    );
  };

  // Sıralama ikonları
  const renderSortIcon = (column) => {
    if (orderBy !== column) return null;
    return order === 'asc' ? ' 🔼' : ' 🔽';
  };

  // Loading durumu
  if (loading && !corporateUser) {
    return (
      <div className="kurumsal-puantaj-container">
        <div className="loading-wrapper">
          <div className="loading-spinner"></div>
          <div className="loading-text">Yükleniyor...</div>
        </div>
      </div>
    );
  }

  // Vergi numarası doğrulama sayfası
  if (!isVerified) {
    return (
      <div className="kurumsal-puantaj-container">
        <div className="kurumsal-header">
          <div className="header-left">
            <h1>Kurumsal Puantaj Görüntüleme</h1>
            <span className="company-badge">{corporateUser?.companyName}</span>
          </div>
          <div className="header-right">
            <span className="user-email">{corporateUser?.email}</span>
            <button className="logout-button" onClick={handleLogout}>
              Çıkış Yap
            </button>
          </div>
        </div>

        <div className="verification-container">
          <div className="verification-card">
            <h2>🔐 Vergi Numarası Doğrulama</h2>
            <p className="verification-description">
              Puantajlarınızı görüntüleyebilmek için vergi numaranızı doğrulamanız gerekmektedir.
              Vergi numaranız, firma sahibinin müşteri listesindeki kayıtla eşleştirilecektir.
            </p>

            <div className="verification-input-group">
              <label>Vergi Numaranız</label>
              <input
                type="text"
                value={taxNumberInput}
                onChange={(e) => setTaxNumberInput(e.target.value.replace(/\D/g, '').slice(0, 11))}
                placeholder="10 veya 11 haneli vergi numarası"
                className="verification-input"
                maxLength="11"
                disabled={verificationLoading}
              />
            </div>

            {searchStep && (
              <div className="search-step">
                <div className="search-spinner"></div>
                <span>{searchStep}</span>
              </div>
            )}

            {error && (
              <div className="verification-error">
                {error}
              </div>
            )}

            <button
              className="verification-button"
              onClick={verifyTaxNumber}
              disabled={verificationLoading || taxNumberInput.length < 10}
            >
              {verificationLoading ? (
                <>
                  <div className="button-spinner"></div>
                  <span>Doğrulanıyor...</span>
                </>
              ) : (
                'Vergi Numarasını Doğrula'
              )}
            </button>

            <div className="verification-info">
              <p>
                <strong>Not:</strong> Eğer vergi numaranız bulunamıyorsa, lütfen 
                puantaj hizmeti aldığınız firma ile iletişime geçin ve müşteri 
                kaydınıza vergi numaranızın eklendiğinden emin olun.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Puantajlar sayfası
  return (
    <div className="kurumsal-puantaj-container">
      <div className="kurumsal-header">
        <div className="header-left">
          <h1>Puantajlarım</h1>
          <span className="company-badge">{matchedCustomerName}</span>
        </div>
        <div className="header-right">
          <span className="user-email">{corporateUser?.email}</span>
          <button className="logout-button" onClick={handleLogout}>
            Çıkış Yap
          </button>
        </div>
      </div>

      <div className="filter-container">
        <div className="filters-row">
          <div className="date-filter">
            <label>Tarih Aralığı: </label>
            <div className="date-pickers">
              <DatePicker
                selected={startDate}
                onChange={debouncedSetStartDate}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                dateFormat="dd.MM.yyyy"
                placeholderText="Başlangıç"
                locale="tr"
              />
              <DatePicker
                selected={endDate}
                onChange={debouncedSetEndDate}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                dateFormat="dd.MM.yyyy"
                placeholderText="Bitiş"
                locale="tr"
              />
            </div>
          </div>

          <div className="machine-filter">
            <label>
              Makine Türü: 
              {selectedMachineTypes.length > 0 && (
                <span className="selection-count">({selectedMachineTypes.length} seçili)</span>
              )}
            </label>
            <Select
              isMulti
              options={machineTypeOptions}
              value={selectedMachineTypes}
              onChange={setSelectedMachineTypes}
              placeholder="Makine Türü Seçin"
              classNamePrefix="react-select"
              closeMenuOnSelect={false}
              maxMenuHeight={200}
            />
          </div>

          <div className="operator-filter">
            <label>
              Operatör: 
              {selectedOperators.length > 0 && (
                <span className="selection-count">({selectedOperators.length} seçili)</span>
              )}
            </label>
            <Select
              isMulti
              options={operatorOptions}
              value={selectedOperators}
              onChange={setSelectedOperators}
              placeholder="Operatör Seçin"
              classNamePrefix="react-select"
              closeMenuOnSelect={false}
              maxMenuHeight={200}
            />
          </div>

          <div className="contractor-filter">
            <label>
              Firma: 
              {selectedContractors.length > 0 && (
                <span className="selection-count">({selectedContractors.length} seçili)</span>
              )}
            </label>
            <Select
              isMulti
              options={contractorOptions}
              value={selectedContractors}
              onChange={setSelectedContractors}
              placeholder="Firma Seçin"
              classNamePrefix="react-select"
              closeMenuOnSelect={false}
              maxMenuHeight={200}
            />
          </div>
        </div>

        <div className="button-group">
          <button onClick={handleSelectAll}>Hepsini Seç</button>
          <button onClick={handleDeselectAll}>Seçimleri Kaldır</button>
          <button
            onClick={() => handleDownloadSelected(false)}
            disabled={selectedPdfs.length === 0 || loading}
            className="download-button-inline"
          >
            {loading ? 'İndiriliyor...' : `ZIP İndir (${selectedPdfs.length})`}
          </button>
          <button
            onClick={() => handleDownloadSelected(true)}
            disabled={selectedPdfs.length === 0 || loading}
            className="download-button-merged"
          >
            {loading ? 'Birleştiriliyor...' : `Birleşik PDF (${selectedPdfs.length})`}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {loading ? (
        <div className="loading-wrapper">
          <div className="loading-spinner"></div>
          <div className="loading-text">Puantajlar yükleniyor...</div>
        </div>
      ) : sortedData.length > 0 ? (
        <>
          <div className="pagination-info">
            <p>
              Toplam {sortedData.length} puantajdan {startIndex + 1}-{Math.min(endIndex, sortedData.length)} arası gösteriliyor
            </p>
          </div>

          <div className="table-container">
            <table className="puantajlar-table">
              <thead>
                <tr>
                  <th>Seç</th>
                  <th>Firma</th>
                  <th>Makine Türü</th>
                  <th onClick={() => handleSort('pdfName')} style={{ cursor: 'pointer' }}>
                    Sözleşme No {renderSortIcon('pdfName')}
                  </th>
                  <th onClick={() => handleSort('Tarih')} style={{ cursor: 'pointer' }}>
                    Tarih {renderSortIcon('Tarih')}
                  </th>
                  <th onClick={() => handleSort('Operatör Adı')} style={{ cursor: 'pointer' }}>
                    Operatör {renderSortIcon('Operatör Adı')}
                  </th>
                  <th>Detaylar</th>
                </tr>
              </thead>
              <tbody>
                {currentPageData.map((item, index) => {
                  const machineCode = item['Makine İsmi'] || '';
                  const machineType = machinesMap[machineCode] || machineCode || '-';
                  
                  return (
                    <tr key={item.id || index}>
                      <td>
                        <input
                          type="checkbox"
                          onChange={() => handleCheckboxChange(item.pdfName)}
                          checked={selectedPdfs.includes(item.pdfName)}
                        />
                      </td>
                      <td>{ownerCompanyName || '-'}</td>
                      <td>{machineType}</td>
                      <td>
                        <button
                          className="link-button"
                          onClick={() => handlePdfPreview(item.pdfRef, item.pdfName)}
                        >
                          {item.pdfName}
                        </button>
                      </td>
                      <td>{formatTrDateOnly(item['Tarih']) || '-'}</td>
                      <td>{item['Operatör Adı'] || '-'}</td>
                      <td>{renderWorkDetails(item)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination-controls">
              <button 
                className="pagination-btn" 
                onClick={handlePreviousPage} 
                disabled={currentPage === 1}
              >
                ← Önceki
              </button>
              
              <div className="pagination-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => {
                  if (
                    pageNumber === 1 || 
                    pageNumber === totalPages || 
                    (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        className={`pagination-number ${currentPage === pageNumber ? 'active' : ''}`}
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    );
                  } else if (
                    pageNumber === currentPage - 3 || 
                    pageNumber === currentPage + 3
                  ) {
                    return <span key={pageNumber} className="pagination-dots">...</span>;
                  }
                  return null;
                })}
              </div>
              
              <button 
                className="pagination-btn" 
                onClick={handleNextPage} 
                disabled={currentPage === totalPages}
              >
                Sonraki →
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="no-data">
          <p>Henüz size ait puantaj kaydı bulunmamaktadır.</p>
        </div>
      )}

      {/* PDF Popup */}
      {selectedPdf && (
        <>
          <div className="pdf-popup-overlay" onClick={closePopup}></div>
          <div className="pdf-popup">
            <div className="pdf-header">
              <h3>{selectedPdf.name}</h3>
              <div className="pdf-header-buttons">
                <button 
                  className="download-button" 
                  onClick={async () => {
                    try {
                      const response = await fetch(selectedPdf.url);
                      const blob = await response.blob();
                      const blobUrl = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = blobUrl;
                      link.download = selectedPdf.name;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(blobUrl);
                    } catch (error) {
                      console.error('PDF indirme hatası:', error);
                    }
                  }}
                  title="PDF'i İndir"
                >
                  📥 İndir
                </button>
                <button className="close-button" onClick={closePopup} title="Kapat">×</button>
              </div>
            </div>
            <iframe
              src={`${selectedPdf.url}#toolbar=0&navpanes=0&scrollbar=0`}
              title={selectedPdf.name}
              width="100%"
              height="100%"
            />
          </div>
        </>
      )}
    </div>
  );
};

export default KurumsalPuantajlar;


